const selectors = {
  config: "[data-country-brand-config]",
  itemTemplate: "[data-country-brand-items]",
  result: "[data-country-brand-filter-result]",
  collectionList: ".collection-list",
  inner: ".collection-list__inner",
  status: "[data-country-brand-status]",
  loader: "[data-country-brand-loader]",
  grid: ".collection-list__collections",
  slider: "scroll-slider-component",
  sliderItems: ".scroll-slider__slider",
  expandWrapper: ".collection-list__expand-button-wrapper",
  productCount: ".collection-item__product-count",
};

const CACHE_NAMESPACE = "country-brand-collection-list:v1";
const CACHE_TTL_MS = 60 * 1000;

class CountryBrandCollectionList extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.abortController = new AbortController();
    this.collectionList = this.closest(selectors.collectionList);
    this.inner = this.collectionList?.querySelector(selectors.inner);
    this.status = this.collectionList?.querySelector(selectors.status);
    this.loader = this.collectionList?.querySelector(selectors.loader);
    this.itemTemplate = this.querySelector(selectors.itemTemplate);

    try {
      this.config = JSON.parse(this.querySelector(selectors.config)?.textContent || "{}");
    } catch (error) {
      this.showError("No se pudo leer la configuración del filtro por país.");
      return;
    }

    this.load();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  async load() {
    const countryIds = (this.config.countryIds || []).filter(Boolean);

    if (!this.collectionList || !this.inner || !this.itemTemplate) {
      this.showError("No se pudo iniciar la lista de marcas por país.");
      return;
    }

    if (!this.config.sourceUrl || !countryIds.length) {
      this.finishLoading();
      return;
    }

    this.collectionList.setAttribute("aria-busy", "true");

    try {
      let result = this.readCachedResult(countryIds);
      const cacheHit = Boolean(result);

      if (!cacheHit) {
        result = await this.fetchIndex(countryIds);

        // Search & Discovery can configure list filters with AND logic. The desired
        // behavior here is always a union, so fall back to one request per country.
        if (countryIds.length > 1 && result.countryOperator === "AND") {
          const countryResults = await Promise.all(countryIds.map((countryId) => this.fetchIndex([countryId])));
          result = this.mergeCountryResults(countryResults);
        }
      }

      if (!result.countryFilterFound) {
        throw new Error("country-filter-missing");
      }

      if (!result.vendorFilterFound) {
        throw new Error("vendor-filter-missing");
      }

      if (!cacheHit) {
        this.writeCachedResult(countryIds, result);
      }

      if (result.activeCountryCount === 0) {
        this.renderCards([]);
        return;
      }

      this.renderCards(result.vendors || []);
    } catch (error) {
      if (error.name === "AbortError") return;

      if (error.message === "country-filter-missing") {
        this.showError("Activa el filtro “País de origen” para la colección origen en Search & Discovery.");
      } else if (error.message === "vendor-filter-missing") {
        this.showError("Activa el filtro “Marca” para la colección origen en Search & Discovery.");
      } else {
        this.showError("No se pudieron cargar las marcas de los países seleccionados.");
      }
    } finally {
      this.finishLoading();
    }
  }

  getCacheKey(countryIds) {
    return `${CACHE_NAMESPACE}:${JSON.stringify({
      sourceUrl: this.config.sourceUrl,
      countryFilterParam: this.config.countryFilterParam,
      countries: [...countryIds].sort(),
      hideOutOfStock: Boolean(this.config.hideOutOfStock),
    })}`;
  }

  readCachedResult(countryIds) {
    if (this.config.designMode) return null;

    const cacheKey = this.getCacheKey(countryIds);

    try {
      const cachedValue = JSON.parse(window.sessionStorage.getItem(cacheKey));

      if (!cachedValue?.result || cachedValue.expiresAt <= Date.now()) {
        window.sessionStorage.removeItem(cacheKey);
        return null;
      }

      return cachedValue.result;
    } catch (error) {
      return null;
    }
  }

  writeCachedResult(countryIds, result) {
    if (this.config.designMode) return;

    try {
      window.sessionStorage.setItem(
        this.getCacheKey(countryIds),
        JSON.stringify({
          expiresAt: Date.now() + CACHE_TTL_MS,
          result,
        }),
      );
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }

  async fetchIndex(countryIds) {
    const url = new URL(this.config.sourceUrl, window.location.origin);
    url.searchParams.delete(this.config.countryFilterParam);
    countryIds.forEach((countryId) => {
      url.searchParams.append(this.config.countryFilterParam, countryId);
    });

    if (this.config.hideOutOfStock) {
      url.searchParams.set(this.config.availabilityFilterParam, "1");
    }

    url.searchParams.set("section_id", this.config.sectionEndpoint);

    const response = await fetch(url.toString(), {
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      signal: this.abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`section-render-${response.status}`);
    }

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, "text/html");
    const resultElement = documentFragment.querySelector(selectors.result);

    if (!resultElement) {
      throw new Error("section-result-missing");
    }

    return JSON.parse(resultElement.textContent);
  }

  mergeCountryResults(results) {
    const vendors = new Map();
    let activeCountryCount = 0;

    results.forEach((result) => {
      if (!result.countryFilterFound || !result.vendorFilterFound || result.activeCountryCount === 0) return;

      activeCountryCount += result.activeCountryCount;
      result.vendors.forEach((vendor) => {
        const existing = vendors.get(vendor.key);
        vendors.set(vendor.key, {
          ...vendor,
          count: (existing?.count || 0) + vendor.count,
        });
      });
    });

    return {
      countryFilterFound: results.every((result) => result.countryFilterFound),
      vendorFilterFound: results.every((result) => result.vendorFilterFound),
      countryOperator: "OR",
      activeCountryCount,
      vendors: Array.from(vendors.values()),
    };
  }

  renderCards(vendors) {
    const itemTarget =
      this.collectionList.querySelector(`${selectors.slider} ${selectors.sliderItems}`) ||
      this.collectionList.querySelector(selectors.grid);
    const vendorMap = new Map(vendors.map((vendor) => [vendor.key, vendor]));
    const fragment = document.createDocumentFragment();
    let renderedCount = 0;

    this.itemTemplate.content.querySelectorAll("[data-filter-key]").forEach((sourceCard) => {
      const vendor = vendorMap.get(sourceCard.dataset.filterKey);
      if (!vendor) return;

      const card = sourceCard.cloneNode(true);
      const productCount = card.querySelector(selectors.productCount);
      renderedCount += 1;

      card.classList.remove("overflow-desktop", "overflow-mobile");

      if (this.config.desktopLimit !== null && renderedCount > this.config.desktopLimit) {
        card.classList.add("overflow-desktop");
      }

      if (this.config.mobileLimit !== null && renderedCount > this.config.mobileLimit) {
        card.classList.add("overflow-mobile");
      }

      if (productCount) {
        productCount.textContent = `(${vendor.count})`;
      }

      fragment.appendChild(card);
    });

    this.updateTruncation(renderedCount);

    if (renderedCount === 0) {
      this.setStatus("No se encontraron colecciones de marca para los países seleccionados.");
      return;
    }

    itemTarget.replaceChildren(fragment);

    const missingCollectionCount = vendors.length - renderedCount;
    if (this.config.designMode && missingCollectionCount > 0) {
      this.setStatus(
        `${missingCollectionCount} marcas coincidentes no tienen una colección compatible con el prefijo configurado.`,
      );
    } else {
      this.setStatus("");
    }

    this.refreshSlider(renderedCount);
  }

  updateTruncation(itemCount) {
    const truncateDesktop = this.config.desktopLimit !== null && itemCount > this.config.desktopLimit;
    const truncateMobile = this.config.mobileLimit !== null && itemCount > this.config.mobileLimit;
    const expandWrapper = this.collectionList.querySelector(selectors.expandWrapper);

    this.inner.dataset.truncateDesktop = String(truncateDesktop);
    this.inner.dataset.truncateMobile = String(truncateMobile);
    this.inner.dataset.expanded = "false";

    if (expandWrapper) {
      expandWrapper.dataset.desktopEnable = String(truncateDesktop);
      expandWrapper.dataset.mobileEnable = String(truncateMobile);
    }
  }

  async refreshSlider(itemCount) {
    const slider = this.collectionList.querySelector(selectors.slider);
    if (!slider || itemCount === 0) return;

    await customElements.whenDefined("scroll-slider-component");

    if (!slider.sliderItems || slider.sliderItems.length === 0) {
      slider.initSlider();
      return;
    }

    const sliderTrack = slider.querySelector(selectors.sliderItems);
    slider.sliderItems = Array.from(sliderTrack.children);
    sliderTrack.dataset.slideCount = String(slider.sliderItems.length);
    slider.initScroller();
  }

  setStatus(message) {
    if (!this.status) return;

    this.status.textContent = message;
    this.status.hidden = !message;
  }

  showError(message) {
    console.warn(`[Country brand collection list] ${message}`);
    this.setStatus(message);
    this.finishLoading();
  }

  finishLoading() {
    if (this.loader) {
      this.loader.hidden = true;
    }

    this.collectionList?.removeAttribute("aria-busy");
  }
}

if (!customElements.get("country-brand-collection-list")) {
  customElements.define("country-brand-collection-list", CountryBrandCollectionList);
}
